import { gql, useApolloClient } from '@apollo/client';
import { useEventCallback } from '@mui/material/utils';
import produce from 'immer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { FileType } from '@work4all/models/lib/File';
import { WidgetsDefinitions } from '@work4all/models/lib/Layout';

import { useEntityEvents } from '../../entity-events/use-entity-events';
import { IResponse, useRefetchOnEntityChanges } from '../../hooks';

import { widgetsRequest } from './widgets-request';

type WidgetData = {
  page: number;
  pageSize: number;
  isLoading: boolean;
  hasMore: boolean;
  data: any[];
  totalCount: number;
  widgetTitle: string;
};

type WidgetsData = Record<string, WidgetData>;

const DEFAULT_WIDGET_DATA: WidgetData = {
  page: 0,
  pageSize: 0,
  isLoading: false,
  hasMore: false,
  data: [],
  totalCount: 0,
  widgetTitle: '',
};

export const useLayoutsData = (
  fileId: number,
  contactId: number | null,
  definitions: WidgetsDefinitions | undefined,
  querySize: number,
  fileType: FileType,
  filterFileWidgetsByFavoriteRecords?: boolean
) => {
  const [loadingGroups, setLoadingGroups] = useState<Record<number, boolean>>(
    {}
  );
  const [widgetsData, setWidgetsData] = useState<WidgetsData>({});
  const client = useApolloClient();

  const requestData = useMemo(() => {
    return widgetsRequest({
      definitions,
      fileId,
      contactId,
      fileType,
      querySize,
      filterFileWidgetsByFavoriteRecords,
    });
  }, [
    definitions,
    fileId,
    contactId,
    fileType,
    querySize,
    filterFileWidgetsByFavoriteRecords,
  ]);

  useEffect(() => {
    const widgetDefs = Object.values(requestData.reqMap);
    const widgetIds = widgetDefs.map((w) => w.id);

    if (requestData.requestByGroup) {
      // Initialize widget data for all widgets, keep old data for widgets that are already loaded.
      setWidgetsData(
        produce((draft) => {
          // Remove data for widgets that are no longer part of the definition
          for (const widgetId of Object.keys(draft)) {
            if (!widgetIds.includes(widgetId)) {
              delete draft[widgetId];
            }
          }

          // Add new data for widgets that are part of the definition
          for (const widgetDef of widgetDefs) {
            const widgetId = widgetDef.id;
            if (!draft[widgetId]) {
              draft[widgetId] = { ...DEFAULT_WIDGET_DATA };
            }

            const widget = draft[widgetId];

            widget.widgetTitle = widgetDef.title;
            widget.page = 0;
            widget.pageSize = querySize;
            widget.isLoading = true;
          }
        })
      );

      const dataSubscriptions = [];

      // Initialize queries for all widgets
      Object.keys(requestData.requestByGroup).forEach(
        (groupRequestsKey, idx) => {
          const groupRequests = requestData.requestByGroup[groupRequestsKey];
          setLoadingGroups((current) => ({ ...current, [idx]: true }));

          const queryBody = groupRequests.join('\n');
          const response = client.watchQuery<Record<string, IResponse>>({
            query: gql`query Widgets${idx} {${queryBody}}`,
            context: { batch: false },
          });

          const sub = response.subscribe((grouped) => {
            // Process and update widget data
            setWidgetsData(
              produce((draft) => {
                // Skip processing if there are GraphQL errors or no data
                if (grouped.error || !grouped.data) {
                  console.error(
                    'Apollo GraphQL error in useLayoutsData:',
                    grouped.error
                  );
                  return;
                }

                for (const [key, response] of Object.entries(grouped.data)) {
                  const widgetDef = requestData.reqMap[key];

                  if (widgetDef && response) {
                    const widgetId = widgetDef.id;

                    const widget = draft[widgetId];

                    // Guard against null/undefined response or response.data
                    if (!widget || !response || response.data == null) {
                      console.warn(
                        `Skipping widget ${widgetId}: invalid response data`
                      );
                      continue;
                    }

                    const oldData = widget.data;
                    const newData = response.data;

                    // If the widget had data when it was reloaded, keep placeholders to fetch it again with fetchMore and keep the scroll state
                    if (oldData.length > newData.length) {
                      widget.data = Array(oldData.length).fill(null);
                      widget.data.splice(0, newData.length, ...newData);
                    } else {
                      widget.data = newData;
                    }

                    widget.page = 0;
                    widget.totalCount = response.total;
                    widget.hasMore = newData.length < response.total;
                    widget.isLoading = grouped.loading;

                    if (widget.data.length > widget.totalCount) {
                      widget.data.length = widget.totalCount;
                    }
                  }
                }
              })
            );

            setLoadingGroups((current) => ({ ...current, [idx]: false }));
          });

          dataSubscriptions.push(sub);
        }
      );

      return () => {
        dataSubscriptions.forEach((sub) => {
          sub.unsubscribe();
        });
      };
    }
  }, [client, querySize, requestData.reqMap, requestData.requestByGroup]);

  const refetchQueries = useEventCallback(() => {
    client.refetchQueries({
      include: Object.keys(requestData.requestByGroup).map(
        (_el, idx) => `Widgets${idx}`
      ),
    });
  });

  useRefetchOnEntityChanges({
    entity: requestData.entities,
    refetch: refetchQueries,
  });

  useEntityEvents((event) => {
    if (event.entity === Entities.favoriteItem) {
      refetchQueries();
    }
  });

  const widgetsFetchMoreStateRef = useRef<Record<string, boolean>>({});

  const fetchMore = useCallback(
    (widgetId: string) => async () => {
      const widget = widgetsData[widgetId];
      if (
        !widget ||
        widget.isLoading ||
        widgetsFetchMoreStateRef.current[widgetId] ||
        !widget.hasMore
      ) {
        return;
      }

      widgetsFetchMoreStateRef.current[widgetId] = true;
      setWidgetsData(
        produce((draft) => {
          draft[widgetId].isLoading = true;
        })
      );

      try {
        const querySize = widget.pageSize;
        const queryPage = widget.page + 1;

        const { allQuery } = widgetsRequest({
          definitions,
          fileId,
          contactId,
          fileType,
          filterFileWidgetsByFavoriteRecords,
          widgetId,
          querySize,
          queryPage,
        });

        const result = await client.query<Record<string, IResponse>>({
          query: gql`query WidgetsFetchMore {${allQuery}}`,
          fetchPolicy: 'network-only',
        });

        // Check for errors in the GraphQL response
        if (result.error) {
          console.error('Apollo GraphQL error in fetchMore:', result.error);
          return;
        }

        // The key is always req1 for single widget queries
        const widgetResult = result.data?.[`req1`];
        const data = widgetResult?.data ?? [];
        const totalCount = widgetResult?.total ?? 0;

        setWidgetsData(
          produce((draft) => {
            const widget = draft[widgetId];

            if (!widget) {
              return;
            }

            const indexStart = queryPage * widget.pageSize;
            const indexEnd = indexStart + data.length;

            widget.page = queryPage;
            widget.data.splice(indexStart, data.length, ...data);
            widget.totalCount = totalCount;
            widget.hasMore = indexEnd + 1 < totalCount;
            widget.isLoading = false;

            if (widget.data.length > widget.totalCount) {
              widget.data.length = widget.totalCount;
            }
          })
        );
      } finally {
        widgetsFetchMoreStateRef.current[widgetId] = false;
        setWidgetsData(
          produce((draft) => {
            const widget = draft[widgetId];

            if (!widget) {
              return;
            }

            widget.isLoading = false;
          })
        );
      }
    },
    [
      widgetsData,
      definitions,
      fileId,
      contactId,
      fileType,
      filterFileWidgetsByFavoriteRecords,
      client,
    ]
  );

  return useMemo(() => {
    return {
      widgetsData,
      fetchMore,
      loading: Object.values(loadingGroups).some((isLoading) => isLoading),
    };
  }, [widgetsData, fetchMore, loadingGroups]);
};
